/**
 * `/leads/new` — Lead capture form.
 *
 * Creates a contact + lead record. Contact dedup (phone/email/externalId)
 * runs server-side; existing contacts are reused automatically.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCreateLead } from "@/hooks/use-leads";

export function LeadNewPage() {
  const navigate = useNavigate();
  const create = useCreateLead();

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    company: "",
    source: "",
  });

  const field = (key: keyof typeof form) => ({
    value: form[key],
    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((prev) => ({ ...prev, [key]: e.target.value })),
  });

  const valid = form.phone.trim() || form.email.trim();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!valid) return;

    const body: Parameters<typeof create.mutate>[0] = {
      metadata: {},
      ...(form.first_name && { first_name: form.first_name }),
      ...(form.last_name && { last_name: form.last_name }),
      ...(form.phone && { phone: form.phone }),
      ...(form.email && { email: form.email }),
      ...(form.company && { company: form.company }),
      ...(form.source && { source: form.source }),
    };

    create.mutate(body, {
      onSuccess: (data) => navigate(`/leads/${data.lead_id}`),
    });
  }

  return (
    <div className="flex flex-col gap-6 max-w-lg">
      <header>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-1">Leads</p>
        <h1 className="text-2xl font-extrabold tracking-tight text-gray-900">Capture lead</h1>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Phone or email is required. Existing contacts are automatically deduped.
        </p>
      </header>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="first_name">First name</Label>
            <Input id="first_name" placeholder="Jane" {...field("first_name")} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="last_name">Last name</Label>
            <Input id="last_name" placeholder="Smith" {...field("last_name")} />
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="phone">Phone <span className="text-muted-foreground">(or email required)</span></Label>
          <Input id="phone" type="tel" placeholder="+15555550100" {...field("phone")} />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" placeholder="jane@example.com" {...field("email")} />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="company">Company</Label>
          <Input id="company" placeholder="Acme Inc." {...field("company")} />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="source">Source</Label>
          <Input id="source" placeholder="website, referral, cold-call…" {...field("source")} />
        </div>

        {create.isError && (
          <p className="text-sm text-destructive">
            {create.error instanceof Error ? create.error.message : "Failed to create lead."}
          </p>
        )}

        <div className="flex gap-3 pt-2">
          <Button type="submit" disabled={!valid || create.isPending}>
            {create.isPending ? "Creating…" : "Create lead"}
          </Button>
          <Button type="button" variant="ghost" onClick={() => navigate("/leads")}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}

export default LeadNewPage;
